import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import Avatar from '../components/Avatar';
import Modal from '../components/Modal';
import { useToast } from '../context/ToastContext';

const KYC_BADGE = {
  pending:  <span className="badge badge-warning">⏳ En attente</span>,
  verified: <span className="badge badge-success">✓ Vérifié</span>,
  rejected: <span className="badge badge-danger">✗ Rejeté</span>,
};

const LIC_BADGE = {
  none:     null,
  pending:  <span className="badge badge-warning">🚗 Permis en attente</span>,
  verified: <span className="badge badge-success">🚗 Permis validé</span>,
  rejected: <span className="badge badge-danger">🚗 Permis rejeté</span>,
};

const API_BASE = process.env.REACT_APP_API_URL || '';

const ROLE_BADGE = {
  client: <span className="badge badge-info">Client</span>,
  provider: <span className="badge badge-secondary">Prestataire</span>,
  admin: <span className="badge" style={{ background: '#f3e8ff', color: '#6b21a8' }}>Admin</span>,
};

export default function Users() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: '', kyc_status: '' });
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v]) => v))).toString();
    api.get(`/admin/users?${params}`)
      .then(r => setUsers(r.data))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(load, [load]);

  const handleKyc = async (id, status) => {
    await api.put(`/admin/users/${id}/kyc`, { kyc_status: status });
    toast(status === 'verified' ? 'KYC validé ✓' : 'KYC rejeté', status === 'verified' ? 'success' : 'warning');
    setSelected(null);
    load();
  };

  const handleLicenseKyc = async (id, status) => {
    await api.put(`/admin/users/${id}/license-kyc`, { license_kyc_status: status });
    toast(status === 'verified' ? 'Permis validé ✓' : 'Permis rejeté', status === 'verified' ? 'success' : 'warning');
    setSelected(null);
    load();
  };

  const handleToggle = async (user) => {
    await api.put(`/admin/users/${user.id}/toggle`);
    toast(user.is_active ? `${user.name} suspendu` : `${user.name} réactivé`, user.is_active ? 'warning' : 'success');
    setSelected(null);
    load();
  };

  const filtered = users.filter(u =>
    !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.phone.includes(search)
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 className="page-title">Gestion des utilisateurs</h2>
          <p className="page-subtitle">{users.length} utilisateurs enregistrés</p>
        </div>
      </div>

      {/* Filters bar */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ padding: '16px 20px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
            <input
              className="form-input"
              style={{ paddingLeft: 36 }}
              placeholder="Rechercher par nom ou téléphone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className="form-input form-select" style={{ width: 160 }} value={filters.role}
            onChange={e => setFilters({ ...filters, role: e.target.value })}>
            <option value="">Tous les rôles</option>
            <option value="client">Clients</option>
            <option value="provider">Prestataires</option>
          </select>
          <select className="form-input form-select" style={{ width: 180 }} value={filters.kyc_status}
            onChange={e => setFilters({ ...filters, kyc_status: e.target.value })}>
            <option value="">Tous les statuts KYC</option>
            <option value="pending">En attente</option>
            <option value="verified">Vérifiés</option>
            <option value="rejected">Rejetés</option>
          </select>
          {(filters.role || filters.kyc_status || search) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilters({ role: '', kyc_status: '' }); setSearch(''); }}>
              ✕ Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton" style={{ height: 64, borderRadius: 8 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">👤</div>
            <p className="empty-state-text">Aucun utilisateur trouvé</p>
          </div>
        </div>
      ) : (
        <div className="table-wrapper animate-fade">
          <table className="table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Téléphone</th>
                <th>Rôle</th>
                <th>KYC</th>
                <th>Statut</th>
                <th>Inscription</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(u)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar src={u.avatar_url} name={u.name} size="sm" />
                      <span style={{ fontWeight: 600 }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{u.phone}</td>
                  <td>{ROLE_BADGE[u.role]}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {KYC_BADGE[u.kyc_status]}
                      {LIC_BADGE[u.license_kyc_status]}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${u.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {u.is_active ? '● Actif' : '● Suspendu'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    {new Date(u.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setSelected(u)}>Voir</button>
                      <button
                        className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => handleToggle(u)}
                      >
                        {u.is_active ? 'Suspendre' : 'Réactiver'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User detail modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Détail utilisateur">
        {selected && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <Avatar src={selected.avatar_url} name={selected.name} size="xl" />
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ fontSize: 18, fontWeight: 700 }}>{selected.name}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{selected.phone}</p>
                {selected.email && <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>{selected.email}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {ROLE_BADGE[selected.role]}
                {KYC_BADGE[selected.kyc_status]}
                <span className={`badge ${selected.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {selected.is_active ? 'Actif' : 'Suspendu'}
                </span>
              </div>
            </div>

            {/* ── Document KYC identité ── */}
            {selected.id_document_url && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Document d'identité</p>
                <a href={`${API_BASE}${selected.id_document_url}`} target="_blank" rel="noreferrer"
                  className="btn btn-secondary btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
                  📄 Voir le document
                </a>
              </div>
            )}

            {/* ── Permis de conduire ── */}
            {(selected.license_front_url || selected.license_back_url) && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>
                    🚗 Permis de conduire
                  </p>
                  {LIC_BADGE[selected.license_kyc_status]}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {selected.license_front_url && (
                    <a href={`${API_BASE}${selected.license_front_url}`} target="_blank" rel="noreferrer" style={{ flex: 1 }}>
                      <img
                        src={`${API_BASE}${selected.license_front_url}`}
                        alt="Recto"
                        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, border: '1.5px solid var(--border)', cursor: 'pointer' }}
                      />
                      <p style={{ fontSize: 11, textAlign: 'center', color: 'var(--text-muted)', marginTop: 4 }}>Recto</p>
                    </a>
                  )}
                  {selected.license_back_url && (
                    <a href={`${API_BASE}${selected.license_back_url}`} target="_blank" rel="noreferrer" style={{ flex: 1 }}>
                      <img
                        src={`${API_BASE}${selected.license_back_url}`}
                        alt="Verso"
                        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, border: '1.5px solid var(--border)', cursor: 'pointer' }}
                      />
                      <p style={{ fontSize: 11, textAlign: 'center', color: 'var(--text-muted)', marginTop: 4 }}>Verso</p>
                    </a>
                  )}
                </div>
                {selected.license_kyc_status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-success btn-sm" style={{ flex: 1, justifyContent: 'center' }}
                      onClick={() => handleLicenseKyc(selected.id, 'verified')}>
                      ✓ Valider le permis
                    </button>
                    <button className="btn btn-danger btn-sm" style={{ flex: 1, justifyContent: 'center' }}
                      onClick={() => handleLicenseKyc(selected.id, 'rejected')}>
                      ✗ Rejeter le permis
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="divider" />

            <div style={{ display: 'flex', gap: 10 }}>
              {selected.kyc_status === 'pending' && selected.role === 'provider' && (
                <>
                  <button className="btn btn-success" style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => handleKyc(selected.id, 'verified')}>
                    ✓ Valider identité
                  </button>
                  <button className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => handleKyc(selected.id, 'rejected')}>
                    ✗ Rejeter identité
                  </button>
                </>
              )}
              <button
                className={`btn ${selected.is_active ? 'btn-danger' : 'btn-success'}`}
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => handleToggle(selected)}
              >
                {selected.is_active ? '🚫 Suspendre' : '✓ Réactiver'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
